import React, { useState, useEffect } from 'react'
import { supabase } from '../utils/supabaseClient'

function IndividualKYCForm({ onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1) // For multi-step form navigation
  const [loading, setLoading] = useState(false) // For form submission state
  const [uploadingDoc, setUploadingDoc] = useState(null) // For document upload state
  const [errors, setErrors] = useState({}) // For form validation errors
  const [successMessage, setSuccessMessage] = useState('') // For success feedback
  const [currentUser, setCurrentUser] = useState(null) // For authenticated user
  const [authLoading, setAuthLoading] = useState(true) // For auth loading state
  const [saving, setSaving] = useState(false) // For draft saving state
  const [saveMessage, setSaveMessage] = useState('') // For draft save feedback

  const [formData, setFormData] = useState({
    // CARD 1 - Personal Information
    fullName: '',
    dateOfBirth: '',
    nationality: '',
    emiratesId: '',
    passportNumber: '',
    visaType: '',
    occupation: '',
    employerName: '',

    
    // CARD 2 - Address & Contact (ADD THESE)
    residentialAddress: '',
    city: '',
    country: '',
    mobileNumber: '',
    email: '',

    // CARD 4 - Source of Funds / Wealth 
    // Employment Income
    employerAddress: '',
    jobTitle: '',
    employmentType: '',
    monthlySalary: '',
    paymentMode: '',
    employmentDuration: '',
    
    // Business Income
    businessName: '',
    licenseNumber: '',
    licenseAuthority: '',
    businessNature: '',
    yearsInOperation: '',
    monthlyTurnover: '',
    primaryBankName: '',
    bankAccountCountry: '',
    
    // Other Sources of Wealth
    wealthSourceType: '',
    wealthDescription: '',
    wealthReceiptDate: '',
    wealthValue: '',
    giverName: '',
    relationshipToGiver: '',
    propertyDescription: '',

    // CARD 5 - Expected Activity (ADD THESE) 🆕
    purposeOfRelationship: '',
    expectedTransactionVolume: '',
    expectedTransactionFrequency: '',
    expectedCounterparties: '',
    anticipatedServices: '',
    
    // CARD 6 - PEP & Consent
    pepStatus: false,
    pepDetails: '',
    consentAccepted: false,

    documents: {
      emiratesIdFront: null, 
      emiratesIdBack: null,
      passport: null,
      visa: null,
      signatureSpecimen: null,
      proofOfAddress: null,
      salarySlip: null,
      bankStatement: null,
      proofOfEmployment: null,
      tradeLicense: null,
      businessIncome: null,
      vatCertificate: null,
      inheritanceDoc: null,
      propertyDoc: null,
      investmentStatement: null
    }
   })

  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        console.log('📍 Session check:', { session, sessionError })
        
        if (sessionError) {
          console.error('Session error:', sessionError)
          setErrors({ submit: 'Session error. Please login again.' })
          setAuthLoading(false)
          return
        }

        if (!session) {
          console.error('No active session found')
          setErrors({ submit: 'No active session. Please login again.' })
          setAuthLoading(false)
          return
        }

        const { data: { user }, error: userError } = await supabase.auth.getUser()
        
        console.log('👤 User from session:', user)
        
        if (userError || !user) {
          console.error('User error:', userError)
          setErrors({ submit: 'Unable to get user data. Please login again.' })
        } else {
          setCurrentUser(user)
          console.log('✅ User loaded:', user.email)
        }

        // 🆕 Load draft if exists
        const { data: draft, error: draftError } = await supabase
          .from('kyc_drafts')
          .select('*')
          .eq('created_by', user.id)
          .eq('type', 'Individual')
          .eq('status', 'DRAFT')
          .order('updated_at', { ascending: false })
          .limit(1)
          .single()

              if (!draftError && draft) {
            console.log('📄 Draft found, loading data...')
            
            // Load form data but clear all document objects
            const loadedData = {
              ...draft.form_data,
              documents: {
                emiratesIdFront: null,
                emiratesIdBack: null,
                passport: null,
                visa: null,
                signatureSpecimen: null,
                proofOfAddress: null,
                salarySlip: null,
                proofOfEmployment: null,
                bankStatement: null,
                tradeLicense: null,
                businessIncome: null,
                vatCertificate: null,
                inheritanceDoc: null,
                propertyDoc: null,
                investmentStatement: null
              }
            }
            
            setFormData(loadedData)
            setCurrentStep(draft.form_data.current_step || 1)
            
            setSaveMessage('📄 Draft loaded! Please re-upload all required documents.')
            setTimeout(() => setSaveMessage(''), 8000)
            }
        } catch (error) {
          console.error('Auth load error:', error)
          setErrors({ submit: 'Authentication error. Please login again.' })
        } finally {
          setAuthLoading(false)
        }
    }

    loadUser()
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Handle file upload
  const handleFileChange = (e, docType) => {
    const file = e.target.files[0]
    
    if (!file) return

    // Validate file size (5MB = 5242880 bytes)
    if (file.size > 5242880) {
      setErrors(prev => ({ ...prev, [docType]: 'File size must be less than 5MB' }))
      e.target.value = null
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [docType]: 'Only JPG, PNG, and PDF files are allowed' }))
      e.target.value = null
      return
    }

    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [docType]: file
      }
    }))

    // Clear error
    setErrors(prev => ({ ...prev, [docType]: '' }))
  }

  // Remove uploaded file
  const removeFile = (docType) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [docType]: null
      }
    }))
  }

    const validateForm = () => {
    const newErrors = {}

    // CARD 1 - Personal Information
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required'
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    } else {
      const dob = new Date(formData.dateOfBirth)
      const today = new Date()
      const age = today.getFullYear() - dob.getFullYear()
      if (age < 18) {
        newErrors.dateOfBirth = 'Customer must be at least 18 years old'
      }
    }
    if (!formData.nationality) newErrors.nationality = 'Nationality is required'
    if (!formData.emiratesId.trim() && !formData.passportNumber.trim()) {
      newErrors.emiratesId = 'Either Emirates ID or Passport Number is required'
      newErrors.passportNumber = 'Either Emirates ID or Passport Number is required'
    }
    if (formData.emiratesId && !/^\d{15}$/.test(formData.emiratesId)) {
      newErrors.emiratesId = 'Emirates ID must be exactly 15 digits'
    }
    if (!formData.occupation) newErrors.occupation = 'Occupation is required'

    // CARD 2 - Address & Contact
    if (!formData.residentialAddress.trim()) newErrors.residentialAddress = 'Residential address is required'
    if (!formData.city.trim()) newErrors.city = 'City is required'
    if (!formData.country) newErrors.country = 'Country is required'
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required'
    } else if (!/^[\+]?[0-9]{10,15}$/.test(formData.mobileNumber.replace(/[\s\-]/g, ''))) {
      newErrors.mobileNumber = 'Please enter a valid mobile number'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!formData.documents.proofOfAddress) {
      newErrors.proofOfAddress = 'Proof of address is required'
    }

    // CARD 3 - ID Documents
    if (formData.emiratesId && !formData.documents.emiratesIdFront) {
      newErrors.emiratesIdFront = 'Emirates ID front is required'
    }
    if (formData.emiratesId && !formData.documents.emiratesIdBack) {
      newErrors.emiratesIdBack = 'Emirates ID back is required'
    }
    if (formData.passportNumber && !formData.documents.passport) {
      newErrors.passport = 'Passport copy is required'
    }
    if (!formData.documents.signatureSpecimen) {
      newErrors.signatureSpecimen = 'Signature specimen is required'
    }

    // CARD 4 - Source of Funds
    if (!formData.sourceOfFunds) {
      newErrors.sourceOfFunds = 'Source of funds is required'
    }

    // CARD 5 - Expected Activity
    if (!formData.purposeOfRelationship) {
      newErrors.purposeOfRelationship = 'Purpose of relationship is required'
    }
    if (!formData.expectedTransactionVolume) {
      newErrors.expectedTransactionVolume = 'Expected transaction volume is required'
    }
    if (!formData.expectedTransactionFrequency) {
      newErrors.expectedTransactionFrequency = 'Expected transaction frequency is required'
    }

    // CARD 6 - PEP & Consent
    if (formData.pepStatus && !formData.pepDetails.trim()) {
      newErrors.pepDetails = 'PEP details are required when you declare PEP status'
    }
    if (!formData.consentAccepted) {
      newErrors.consentAccepted = 'You must accept the data privacy consent to proceed'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Upload document to Supabase Storage
    const uploadDocument = async (file, customerId, docType) => {
    try {
      // Add safety checks for file object
      if (!file) {
        console.error(`❌ No file provided for ${docType}`)
        throw new Error(`No file provided for ${docType}`)
      }

      // Handle both File objects and draft-restored objects
      const fileName = file.name || `document_${Date.now()}.pdf`
      const fileType = file.type || 'application/pdf'
      const fileSize = file.size || 0

      // Get file extension safely
      const fileExt = fileName.includes('.') ? fileName.split('.').pop() : 'pdf'
      const uploadFileName = `${customerId}/${docType}_${Date.now()}.${fileExt}`

      console.log(`📤 Uploading ${docType}:`, uploadFileName)

      const { data, error } = await supabase.storage
        .from('kyc-documents')
        .upload(uploadFileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) throw error

      console.log(`✅ ${docType} uploaded:`, data.path)

      // Save document metadata to database
      const { error: dbError } = await supabase
        .from('client_documents')
        .insert([{
          client_id: customerId,
          document_type: docType,
          file_name: fileName,
          file_size: fileSize,
          file_type: fileType,
          storage_path: data.path,
          uploaded_by_user_id: currentUser.id
        }])

      if (dbError) throw dbError

      return data.path
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error)
      throw error
    }
  }

  // Navigation between steps
    const goToNextStep = () => {
    // Allow navigation without validation - just move to next step
    setCurrentStep(prev => prev + 1)
    window.scrollTo(0, 0)
    
    // Clear any validation errors when moving forward
    setErrors({})
  }

  const goToPreviousStep = () => {
    setCurrentStep(prev => prev - 1)
    window.scrollTo(0, 0)
  }

  const validateCard1 = () => {
    const newErrors = {}

    // CARD 1 - Personal Information Validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }
    
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    } else {
      const dob = new Date(formData.dateOfBirth)
      const today = new Date()
      const age = today.getFullYear() - dob.getFullYear()
      if (age < 18) {
        newErrors.dateOfBirth = 'Customer must be at least 18 years old'
      }
    }
    
    if (!formData.nationality) {
      newErrors.nationality = 'Nationality is required'
    }
    
    if (!formData.emiratesId.trim() && !formData.passportNumber.trim()) {
      newErrors.emiratesId = 'Either Emirates ID or Passport Number is required'
      newErrors.passportNumber = 'Either Emirates ID or Passport Number is required'
    }
    
    if (formData.emiratesId && !/^\d{15}$/.test(formData.emiratesId)) {
      newErrors.emiratesId = 'Emirates ID must be exactly 15 digits'
    }
    
    if (!formData.occupation) {
      newErrors.occupation = 'Occupation is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  //Card 2 Validation function
  const validateCard2 = () => {
  const newErrors = {}

  if (!formData.residentialAddress.trim()) {
    newErrors.residentialAddress = 'Residential address is required'
  }
  
  if (!formData.city.trim()) {
    newErrors.city = 'City is required'
  }
  if (!formData.country) {
    newErrors.country = 'Country is required'
  }
  
  if (!formData.mobileNumber.trim()) {
    newErrors.mobileNumber = 'Mobile number is required'
  } else if (!/^[\+]?[0-9]{10,15}$/.test(formData.mobileNumber.replace(/[\s\-]/g, ''))) {
    newErrors.mobileNumber = 'Please enter a valid mobile number'
  }
  
  if (!formData.email.trim()) {
    newErrors.email = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    newErrors.email = 'Please enter a valid email address'
  }
  
  if (!formData.documents.proofOfAddress) {
    newErrors.proofOfAddress = 'Proof of address is required'
  }

  setErrors(newErrors)
  return Object.keys(newErrors).length === 0
  }

  // Card validation functions to be above here


  // Draft saving function
    const handleSaveDraft = async () => {
    if (!currentUser) {
      setErrors({ submit: 'User session not loaded. Please close and reopen the form.' })
      return
    }

    setSaving(true)
    setSaveMessage('')

    try {
      console.log('💾 Saving draft with user:', currentUser.id, currentUser.email)

      // Check if draft already exists
      const { data: existingDraft, error: fetchError } = await supabase
        .from('kyc_drafts')
        .select('id')
        .eq('created_by', currentUser.id)
        .eq('type', 'Individual')
        .eq('status', 'DRAFT')
        .single()

      // 🆕 Create a copy of formData WITHOUT file objects (they can't be serialized)
      const formDataForDraft = {
        ...formData,
        documents: {
          // Store only file metadata, not the actual file objects
          emiratesIdFront: formData.documents.emiratesIdFront ? { 
            name: formData.documents.emiratesIdFront.name,
            size: formData.documents.emiratesIdFront.size,
            type: formData.documents.emiratesIdFront.type,
            hasFile: true
          } : null,
          emiratesIdBack: formData.documents.emiratesIdBack ? { 
            name: formData.documents.emiratesIdBack.name,
            size: formData.documents.emiratesIdBack.size,
            type: formData.documents.emiratesIdBack.type,
            hasFile: true
          } : null,
          passport: formData.documents.passport ? { 
            name: formData.documents.passport.name,
            size: formData.documents.passport.size,
            type: formData.documents.passport.type,
            hasFile: true
          } : null,
          visa: formData.documents.visa ? { 
            name: formData.documents.visa.name,
            size: formData.documents.visa.size,
            type: formData.documents.visa.type,
            hasFile: true
          } : null,
          signatureSpecimen: formData.documents.signatureSpecimen ? { 
            name: formData.documents.signatureSpecimen.name,
            size: formData.documents.signatureSpecimen.size,
            type: formData.documents.signatureSpecimen.type,
            hasFile: true
          } : null,
          proofOfAddress: formData.documents.proofOfAddress ? { 
            name: formData.documents.proofOfAddress.name,
            size: formData.documents.proofOfAddress.size,
            type: formData.documents.proofOfAddress.type,
            hasFile: true
          } : null,
          salarySlip: formData.documents.salarySlip ? { 
            name: formData.documents.salarySlip.name,
            size: formData.documents.salarySlip.size,
            type: formData.documents.salarySlip.type,
            hasFile: true
          } : null,
          proofOfEmployment: formData.documents.proofOfEmployment ? { 
            name: formData.documents.proofOfEmployment.name,
            size: formData.documents.proofOfEmployment.size,
            type: formData.documents.proofOfEmployment.type,
            hasFile: true
          } : null,
          bankStatement: formData.documents.bankStatement ? { 
            name: formData.documents.bankStatement.name,
            size: formData.documents.bankStatement.size,
            type: formData.documents.bankStatement.type,
            hasFile: true
          } : null,
          tradeLicense: formData.documents.tradeLicense ? { 
            name: formData.documents.tradeLicense.name,
            size: formData.documents.tradeLicense.size,
            type: formData.documents.tradeLicense.type,
            hasFile: true
          } : null,
          businessIncome: formData.documents.businessIncome ? { 
            name: formData.documents.businessIncome.name,
            size: formData.documents.businessIncome.size,
            type: formData.documents.businessIncome.type,
            hasFile: true
          } : null,
          vatCertificate: formData.documents.vatCertificate ? { 
            name: formData.documents.vatCertificate.name,
            size: formData.documents.vatCertificate.size,
            type: formData.documents.vatCertificate.type,
            hasFile: true
          } : null,
          inheritanceDoc: formData.documents.inheritanceDoc ? { 
            name: formData.documents.inheritanceDoc.name,
            size: formData.documents.inheritanceDoc.size,
            type: formData.documents.inheritanceDoc.type,
            hasFile: true
          } : null,
          propertyDoc: formData.documents.propertyDoc ? { 
            name: formData.documents.propertyDoc.name,
            size: formData.documents.propertyDoc.size,
            type: formData.documents.propertyDoc.type,
            hasFile: true
          } : null,
          investmentStatement: formData.documents.investmentStatement ? { 
            name: formData.documents.investmentStatement.name,
            size: formData.documents.investmentStatement.size,
            type: formData.documents.investmentStatement.type,
            hasFile: true
          } : null
        }
      }

      const draftData = {
        type: 'Individual',
        status: 'DRAFT',
        form_data: formDataForDraft,
        current_step: currentStep,
        created_by: currentUser.id,
        updated_at: new Date().toISOString()
      }

      if (existingDraft) {
        // Update existing draft
        const { error: updateError } = await supabase
          .from('kyc_drafts')
          .update(draftData)
          .eq('id', existingDraft.id)

        if (updateError) throw updateError
        console.log('✅ Draft updated:', existingDraft.id)
      } else {
        // Create new draft
        draftData.created_at = new Date().toISOString()
        
        const { error: insertError } = await supabase
          .from('kyc_drafts')
          .insert([draftData])

        if (insertError) throw insertError
        console.log('✅ New draft created')
      }

      setSaveMessage('✓ Draft saved successfully! (Files will need to be re-uploaded)')
      setTimeout(() => setSaveMessage(''), 5000)

      } catch (error) {
      console.error('❌ Error saving draft:', error)
      setErrors({ submit: error.message || 'Failed to save draft. Please try again.' })
      } finally {
      setSaving(false)
      }
    }

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault()


    // Trying debugging with another way:
      // Run existing validation (boolean)
        const isValid = validateForm();

        if (!isValid) {
          // Try to collect useful diagnostic info from the form element that triggered submit.
          // This relies on the form's inputs having `name` attributes (standard practice).
          const formEl = e.target && e.target.tagName === 'FORM' ? e.target : null;
          const invalidFields = {};
          if (formEl) {
            // HTMLConstraintValidation API to find invalid fields (browser native)
            // and also log empty required fields.
            for (const el of Array.from(formEl.elements)) {
              if (!el.name) continue; // we only care about named inputs
              // Skip buttons
              if (el.tagName === 'BUTTON' || el.type === 'submit' || el.type === 'button') continue;

              // Check validity (built-in checks like required, pattern etc.)
              if (typeof el.checkValidity === 'function' && !el.checkValidity()) {
                invalidFields[el.name] = el.validationMessage || 'Invalid value';
                continue;
              }

              // If your validateForm uses custom rules (e.g. file presence), check common cases:
              if (el.required && (el.value === undefined || el.value === null || String(el.value).trim() === '')) {
                invalidFields[el.name] = 'Required';
                continue;
              }

              // If it's a file input, ensure a file is present when required
              if (el.type === 'file') {
                const files = el.files;
                if (el.required && (!files || files.length === 0)) {
                  invalidFields[el.name] = 'File required';
                }
              }
            }
          }

          // If we couldn't inspect the form or invalidFields is empty, at least log form state you have
          console.error('Validation failed — validateForm() returned false.');
          if (Object.keys(invalidFields).length) {
            console.error('Field-level validation issues:', invalidFields);
            // Optionally set field-level errors in state so UI can render them
            // Assumes you keep errors as an object in component state: setErrors/ setFormErrors
            if (typeof setErrors === 'function') setErrors(prev => ({ ...prev, ...invalidFields }));
          } else {
            // Fallback: dump the values of named inputs so you can inspect what's submitted
            if (formEl) {
              const values = {};
              for (const el of Array.from(formEl.elements)) {
                if (!el.name) continue;
                if (el.type === 'file') {
                  values[el.name] = el.files ? `${el.files.length} file(s)` : 'no-files';
                } else {
                  values[el.name] = el.value;
                }
              }
              console.error('Form values at time of validation failure:', values);
              if (typeof setErrors === 'function') setErrors(prev => ({ ...prev, submit: 'Please check highlighted fields.' }));
            } else {
              // final fallback
              console.error('Could not access form element to report more details.');
              if (typeof setErrors === 'function') setErrors({ submit: 'Validation failed. Could not get field details.' });
            }
          }

          // stop the submit flow
          return;
        }

        // validation passed: continue to the rest of your flow
        setLoading(true);

    // if (!validateForm()) {
    //   console.log('Validation failed')
    //   return
    // }

    // if (!currentUser) {
    //   setErrors({ submit: 'User session not loaded. Please close and reopen the form.' })
    //   return
    // }

    // setLoading(true)

    try {
      console.log('✅ Submitting with user:', currentUser.id, currentUser.email)

      // 1. Create customer record
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .insert([{
          type: 'Individual',
          status: 'SUBMITTED',
          created_by: currentUser.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (customerError) {
        console.error('❌ Customer insert error:', customerError)
        if (customerError.code === '42501' || customerError.code === 'PGRST301') {
          throw new Error('🔒 Permission denied. Your user role does not have access.')
        }
        throw customerError
      }

      const customerId = customerData.id
      console.log('✅ Customer created:', customerId)

      // 2. Upload documents
      console.log('📤 Starting document uploads...')
      const docUploads = []

      // Helper function to check if it's a real File object
      const isRealFile = (file) => {
        return file instanceof File || (file instanceof Blob)
      }

      // Card 3 documents
      if (formData.documents.emiratesIdFront && isRealFile(formData.documents.emiratesIdFront)) {
        setUploadingDoc('Emirates ID (Front)')
        docUploads.push(uploadDocument(formData.documents.emiratesIdFront, customerId, 'emirates_id_front'))
      }
      if (formData.documents.emiratesIdBack && isRealFile(formData.documents.emiratesIdBack)) {
        setUploadingDoc('Emirates ID (Back)')
        docUploads.push(uploadDocument(formData.documents.emiratesIdBack, customerId, 'emirates_id_back'))
      }
      if (formData.documents.passport && isRealFile(formData.documents.passport)) {
        setUploadingDoc('Passport')
        docUploads.push(uploadDocument(formData.documents.passport, customerId, 'passport'))
      }
      if (formData.documents.visa && isRealFile(formData.documents.visa)) {
        setUploadingDoc('Visa/Residence Permit')
        docUploads.push(uploadDocument(formData.documents.visa, customerId, 'visa'))
      }
      if (formData.documents.signatureSpecimen && isRealFile(formData.documents.signatureSpecimen)) {
        setUploadingDoc('Signature Specimen')
        docUploads.push(uploadDocument(formData.documents.signatureSpecimen, customerId, 'signature_specimen'))
      }

      // Card 2 documents
      if (formData.documents.proofOfAddress && isRealFile(formData.documents.proofOfAddress)) {
        setUploadingDoc('Proof of Address')
        docUploads.push(uploadDocument(formData.documents.proofOfAddress, customerId, 'proof_of_address'))
      }

      // Card 4 - Employment documents
      if (formData.documents.proofOfEmployment && isRealFile(formData.documents.proofOfEmployment)) {
        setUploadingDoc('Proof of Employment')
        docUploads.push(uploadDocument(formData.documents.proofOfEmployment, customerId, 'proof_of_employment'))
      }
      if (formData.documents.bankStatement && isRealFile(formData.documents.bankStatement)) {
        setUploadingDoc('Bank Statement')
        docUploads.push(uploadDocument(formData.documents.bankStatement, customerId, 'bank_statement'))
      }

      // Card 4 - Business documents
      if (formData.documents.tradeLicense && isRealFile(formData.documents.tradeLicense)) {
        setUploadingDoc('Trade License')
        docUploads.push(uploadDocument(formData.documents.tradeLicense, customerId, 'trade_license'))
      }
      if (formData.documents.businessIncome && isRealFile(formData.documents.businessIncome)) {
        setUploadingDoc('Business Income Proof')
        docUploads.push(uploadDocument(formData.documents.businessIncome, customerId, 'business_income'))
      }
      if (formData.documents.vatCertificate && isRealFile(formData.documents.vatCertificate)) {
        setUploadingDoc('VAT Certificate')
        docUploads.push(uploadDocument(formData.documents.vatCertificate, customerId, 'vat_certificate'))
      }

      // Card 4 - Other wealth documents
      if (formData.documents.inheritanceDoc && isRealFile(formData.documents.inheritanceDoc)) {
        setUploadingDoc('Inheritance Documentation')
        docUploads.push(uploadDocument(formData.documents.inheritanceDoc, customerId, 'inheritance_doc'))
      }
      if (formData.documents.propertyDoc && isRealFile(formData.documents.propertyDoc)) {
        setUploadingDoc('Property Documentation')
        docUploads.push(uploadDocument(formData.documents.propertyDoc, customerId, 'property_doc'))
      }
      if (formData.documents.investmentStatement && isRealFile(formData.documents.investmentStatement)) {
        setUploadingDoc('Investment Statement')
        docUploads.push(uploadDocument(formData.documents.investmentStatement, customerId, 'investment_statement'))
      }

      // Check if we have any files to upload
      if (docUploads.length === 0) {
        console.warn('⚠️ No fresh files to upload. Please re-upload documents.')
        throw new Error('No documents to upload. Please re-upload all required documents.')
      }

      await Promise.all(docUploads)
      setUploadingDoc(null)
      console.log('✅ All documents uploaded')

      // 3. Save KYC data
      const { error: kycError } = await supabase
        .from('kyc_individual')
        .insert([{
          customer_id: customerId, 
           // CARD 1 - Personal Information
          full_name: formData.fullName,
          date_of_birth: formData.dateOfBirth,
          nationality: formData.nationality,
          emirates_id: formData.emiratesId || null,
          passport_number: formData.passportNumber || null,
          visa_type: formData.visaType || null,
          occupation: formData.occupation,
          employer_name: formData.employerName || null,
      
          // CARD 2 - Address & Contact Information
          residential_address: formData.residentialAddress,
          city: formData.city,
          country: formData.country,
          mobile_number: formData.mobileNumber,
          email: formData.email,

          // CARD 3 - Documents Upload (handled via uploadDocument function)
          // CARD 4 - Source of Funds / Wealth (ADD THESE) 🆕
          source_of_funds: formData.sourceOfFunds,
          
          // Employment Income
          employer_address: formData.employerAddress || null,
          job_title: formData.jobTitle || null,
          employment_type: formData.employmentType || null,
          monthly_salary: formData.monthlySalary || null,
          payment_mode: formData.paymentMode || null,
          employment_duration: formData.employmentDuration || null,
          
          // Business Income
          business_name: formData.businessName || null,
          license_number: formData.licenseNumber || null,
          license_authority: formData.licenseAuthority || null,
          business_nature: formData.businessNature || null,
          years_in_operation: formData.yearsInOperation || null,
          monthly_turnover: formData.monthlyTurnover || null,
          primary_bank_name: formData.primaryBankName || null,
          bank_account_country: formData.bankAccountCountry || null,
          
          // Other Sources of Wealth
          wealth_source_type: formData.wealthSourceType || null,
          wealth_description: formData.wealthDescription || null,
          wealth_receipt_date: formData.wealthReceiptDate || null,
          wealth_value: formData.wealthValue || null,
          giver_name: formData.giverName || null,
          relationship_to_giver: formData.relationshipToGiver || null,
          property_description: formData.propertyDescription || null,

          // CARD 5 - Expected Activity (ADD THESE) 🆕
          purpose_of_relationship: formData.purposeOfRelationship,
          expected_transaction_volume: formData.expectedTransactionVolume || null,
          expected_transaction_frequency: formData.expectedTransactionFrequency || null,
          expected_counterparties: formData.expectedCounterparties || null,
          anticipated_services: formData.anticipatedServices || null,
          
          // CARD 6 - PEP & Consent (ADD THESE) 🆕
          pep_flag: formData.pepStatus,
          pep_details: formData.pepDetails || null,
          consent_accepted: formData.consentAccepted,
          consent_timestamp: new Date().toISOString(),
          consent_version: '1.0',
          created_at: new Date().toISOString()
        }])

      if (kycError) throw kycError
      console.log('✅ KYC data saved')

      // 4. Create risk score entry
      await supabase.from('risk_scores').insert([{
        customer_id: customerId,
        status: 'Pending',
        created_at: new Date().toISOString()
      }])

      // 5. Log audit trail
      await supabase.from('audit_trail').insert([{
        customer_id: customerId,
        action_type: 'CREATE',
        entity_type: 'KYC_Individual',
        user_id: currentUser.id,
        user_email: currentUser.email || 'unknown',
        change_summary: `Individual KYC created: ${formData.fullName}`,
        details: { formData: { ...formData, documents: 'uploaded' } },
        created_at: new Date().toISOString()
      }])

      setSuccessMessage('✓ Individual KYC submitted successfully!')
      
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(customerId, 'individual')
        }
      }, 1500)

    } catch (error) {
      console.error('❌ Error submitting form:', error)
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' })
    } finally {
      setLoading(false)
      setUploadingDoc(null)
    }
  }

  if (authLoading) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000
      }}>
        <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '40px', textAlign: 'center' }}>
          <h3>Loading user session...</h3>
          <p style={{ color: '#666' }}>Please wait</p>
        </div>
      </div>
    )
  }

  if (!currentUser && errors.submit) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '10px', padding: '40px',
          textAlign: 'center', maxWidth: '500px'
        }}>
          <h3 style={{ color: '#f44336' }}>Authentication Error</h3>
          <p style={{ color: '#666', margin: '20px 0' }}>{errors.submit}</p>
          <button onClick={onClose} style={{
            backgroundColor: '#1976d2', color: 'white', border: 'none',
            padding: '12px 24px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold'
          }}>
            Close and Login Again
          </button>
        </div>
      </div>
    )
  }

  // File upload field component
  const FileUploadField = ({ label, docType, required, helpText }) => (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
        {label} {required && '*'}
        {helpText && <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>{helpText}</span>}
      </label>
      
      {!formData.documents[docType] ? (
        <div>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => handleFileChange(e, docType)}
            style={{ width: '100%', padding: '10px', border: errors[docType] ? '2px solid #f44336' : '1px solid #ccc', borderRadius: '5px', fontSize: '14px' }}
          />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            Max 5MB • JPG, PNG, PDF
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
          <span style={{ flex: 1, fontSize: '14px' }}>
            ✓ {formData.documents[docType].name} ({(formData.documents[docType].size / 1024).toFixed(1)} KB)
          </span>
          <button
            type="button"
            onClick={() => removeFile(docType)}
            style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          >
            Remove
          </button>
        </div>
      )}
      {errors[docType] && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors[docType]}</p>}
    </div>
  )

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
      overflow: 'auto', padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '10px', width: '100%',
        maxWidth: '800px', maxHeight: '90vh', overflow: 'auto', padding: '30px'
      }}>
        <div style={{
          backgroundColor: '#e3f2fd', padding: '10px 15px', borderRadius: '5px',
          marginBottom: '15px', fontSize: '14px'
        }}>
          ✅ Logged in as: <strong>{currentUser?.email || 'Unknown'}</strong>
        </div>

          {/* SAVE MESSAGE */}
          {saveMessage && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#ff9800', 
            color: 'white',
            borderRadius: '5px', 
            marginBottom: '20px', 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>💾</span> {saveMessage}
          </div>
          )}

          {/* Progress Indicator */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '30px',
            position: 'relative'
          }}>
            {/* Progress Bar Background */}
            <div style={{
              position: 'absolute',
              top: '15px',
              left: '0',
              right: '0',
              height: '2px',
              backgroundColor: '#e0e0e0',
              zIndex: 0
            }} />
            
            {/* Progress Bar Fill */}
            <div style={{
              position: 'absolute',
              top: '15px',
              left: '0',
              width: `${((currentStep - 1) / 5) * 100}%`,
              height: '2px',
              backgroundColor: '#1976d2',
              zIndex: 1,
              transition: 'width 0.3s ease'
            }} />

            {[
              { num: 1, label: 'Personal Info' },
              { num: 2, label: 'Address' },
              { num: 3, label: 'Documents' },
              { num: 4, label: 'Source of Funds' },
              { num: 5, label: 'Activity' },
              { num: 6, label: 'Consent' }
            ].map((step) => (
              <div key={step.num} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                flex: 1,
                position: 'relative',
                zIndex: 2
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: currentStep >= step.num ? '#1976d2' : '#e0e0e0',
                  color: currentStep >= step.num ? 'white' : '#999',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '8px',
                  transition: 'all 0.3s ease'
                }}>
                  {currentStep > step.num ? '✓' : step.num}
                </div>
                <span style={{ 
                  fontSize: '11px', 
                  color: currentStep >= step.num ? '#0d47a1' : '#999',
                  textAlign: 'center',
                  fontWeight: currentStep === step.num ? 'bold' : 'normal'
                }}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

        <h2 style={{ color: '#0d47a1', marginBottom: '10px' }}>Individual KYC Form</h2>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
          All fields marked with * are required as per UAE AML regulations
        </p>

        {successMessage && (
          <div style={{ padding: '15px', backgroundColor: '#4caf50', color: 'white',
            borderRadius: '5px', marginBottom: '20px', fontWeight: 'bold' }}>
            {successMessage}
          </div>
        )}

        {errors.submit && (
          <div style={{ padding: '15px', backgroundColor: '#f44336', color: 'white',
            borderRadius: '5px', marginBottom: '20px' }}>
            {errors.submit}
          </div>
        )}

        {uploadingDoc && (
          <div style={{ padding: '15px', backgroundColor: '#ff9800', color: 'white',
            borderRadius: '5px', marginBottom: '20px', fontWeight: 'bold' }}>
            📤 Uploading: {uploadingDoc}...
          </div>
        )}

        {/* Form Submission Code with UI */}
        <form onSubmit={handleSubmit}>
  
          {/* CARD 1 - PERSONAL INFORMATION */}
          {currentStep === 1 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 1 – Personal Information
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.3, FATF Rec.10, MoE Rulebook)
                </span>
              </h3>

              {/* Full Legal Name */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Full Legal Name *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Must match Emirates ID or Passport
                  </span>
                </label>
                <input 
                  type="text" 
                  name="fullName" 
                  value={formData.fullName} 
                  onChange={handleChange}
                  placeholder="Enter full legal name as per ID" 
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.fullName ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                />
                {errors.fullName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.fullName}</p>}
              </div>

              {/* Date of Birth */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Date of Birth *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Must be ≥18 years; cross-check ID
                  </span>
                </label>
                <input 
                  type="date" 
                  name="dateOfBirth" 
                  value={formData.dateOfBirth} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.dateOfBirth ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                />
                {errors.dateOfBirth && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.dateOfBirth}</p>}
              </div>

              {/* Nationality */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Nationality *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    ISO country code; used in risk scoring
                  </span>
                </label>
                <select 
                  name="nationality" 
                  value={formData.nationality} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.nationality ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select nationality</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="SA">Saudi Arabia</option>
                  <option value="KW">Kuwait</option>
                  <option value="QA">Qatar</option>
                  <option value="BH">Bahrain</option>
                  <option value="OM">Oman</option>
                  <option value="IN">India</option>
                  <option value="PK">Pakistan</option>
                  <option value="BD">Bangladesh</option>
                  <option value="PH">Philippines</option>
                  <option value="EG">Egypt</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                  <option value="OTHER">Other</option>
                </select>
                {errors.nationality && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.nationality}</p>}
              </div>

              {/* Emirates ID Number */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Emirates ID Number *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Checksum + OCR validation (CBUAE Art.3)
                  </span>
                </label>
                <input 
                  type="text" 
                  name="emiratesId" 
                  value={formData.emiratesId} 
                  onChange={handleChange}
                  placeholder="784-XXXX-XXXXXXX-X (15 digits)" 
                  maxLength="15"
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.emiratesId ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                />
                {errors.emiratesId && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.emiratesId}</p>}
              </div>

              {/* Passport Number */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Passport Number
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    (non-UAE) Regex + expiry validation (FATF Rec.10)
                  </span>
                </label>
                <input 
                  type="text" 
                  name="passportNumber" 
                  value={formData.passportNumber} 
                  onChange={handleChange}
                  placeholder="Enter passport number" 
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.passportNumber ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                />
                {errors.passportNumber && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.passportNumber}</p>}
              </div>

              {/* Visa Type (for expats) */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Visa Type
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    (expats) Must match ID (CBUAE Art.3)
                  </span>
                </label>
                <select 
                  name="visaType" 
                  value={formData.visaType} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.visaType ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select visa type</option>
                  <option value="Employment">Employment Visa</option>
                  <option value="Residence">Residence Visa</option>
                  <option value="Investor">Investor Visa</option>
                  <option value="Partner">Partner Visa</option>
                  <option value="Golden">Golden Visa</option>
                  <option value="Tourist">Tourist Visa</option>
                  <option value="NA">Not Applicable (UAE National)</option>
                </select>
                {errors.visaType && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.visaType}</p>}
              </div>

              {/* Occupation */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Occupation *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Use standardized job taxonomy (FATF Rec.10)
                  </span>
                </label>
                <select 
                  name="occupation" 
                  value={formData.occupation} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.occupation ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select occupation</option>
                  <option value="Manager">Manager / Executive</option>
                  <option value="Professional">Professional (Doctor, Engineer, Lawyer)</option>
                  <option value="Technician">Technician / Associate Professional</option>
                  <option value="Clerical">Clerical Support Worker</option>
                  <option value="Service">Service / Sales Worker</option>
                  <option value="Agriculture">Agriculture / Fishery Worker</option>
                  <option value="Craft">Craft / Trade Worker</option>
                  <option value="Operator">Plant / Machine Operator</option>
                  <option value="Elementary">Elementary Occupation</option>
                  <option value="Business">Business Owner</option>
                  <option value="Self-Employed">Self-Employed</option>
                  <option value="Student">Student</option>
                  <option value="Retired">Retired</option>
                  <option value="Unemployed">Unemployed</option>
                  <option value="Other">Other</option>
                </select>
                {errors.occupation && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.occupation}</p>}
              </div>

              {/* Employer Name */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Employer Name
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Cross-check with sanctions / PEP lists (CBUAE Art.3)
                  </span>
                </label>
                <input 
                  type="text" 
                  name="employerName" 
                  value={formData.employerName} 
                  onChange={handleChange}
                  placeholder="Enter employer/company name" 
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.employerName ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                />
                {errors.employerName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.employerName}</p>}
              </div>
            </div>
          )}

          
          {/* CARD 2 - ADDRESS & CONTACT */}
            {currentStep === 2 && (
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ 
                  color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                  borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
                }}>
                  CARD 2 – Address & Contact
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                    (CBUAE Art.3, FATF Rec.10)
                  </span>
                </h3>

                {/* Residential Address */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Residential Address *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      Verify against proof document (CBUAE Art.3)
                    </span>
                  </label>
                  <textarea 
                    name="residentialAddress" 
                    value={formData.residentialAddress} 
                    onChange={handleChange}
                    placeholder="Enter complete residential address including building, street, area" 
                    rows="3"
                    style={{
                      width: '100%', 
                      padding: '10px',
                      border: errors.residentialAddress ? '2px solid #f44336' : '1px solid #ccc',
                      borderRadius: '5px', 
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                  {errors.residentialAddress && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.residentialAddress}</p>}
                </div>

                {/* City */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    City *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      Must match proof document (CBUAE Art.3)
                    </span>
                  </label>
                  <input 
                    type="text" 
                    name="city" 
                    value={formData.city} 
                    onChange={handleChange}
                    placeholder="Enter city" 
                    style={{
                      width: '100%', 
                      padding: '10px',
                      border: errors.city ? '2px solid #f44336' : '1px solid #ccc',
                      borderRadius: '5px', 
                      fontSize: '14px'
                    }}
                  />
                  {errors.city && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.city}</p>}
                </div>

                {/* Country */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Country *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      Used for jurisdiction risk (FATF Rec.10)
                    </span>
                  </label>
                  <select 
                    name="country" 
                    value={formData.country} 
                    onChange={handleChange}
                    style={{
                      width: '100%', 
                      padding: '10px',
                      border: errors.country ? '2px solid #f44336' : '1px solid #ccc',
                      borderRadius: '5px', 
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select country</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="KW">Kuwait</option>
                    <option value="QA">Qatar</option>
                    <option value="BH">Bahrain</option>
                    <option value="OM">Oman</option>
                    <option value="IN">India</option>
                    <option value="PK">Pakistan</option>
                    <option value="BD">Bangladesh</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {errors.country && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.country}</p>}
                </div>

                {/* Mobile Number */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Mobile Number *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      OTP verification required (FATF Rec.10)
                    </span>
                  </label>
                  <input 
                    type="tel" 
                    name="mobileNumber" 
                    value={formData.mobileNumber} 
                    onChange={handleChange}
                    placeholder="+971-XX-XXX-XXXX" 
                    style={{
                      width: '100%', 
                      padding: '10px',
                      border: errors.mobileNumber ? '2px solid #f44336' : '1px solid #ccc',
                      borderRadius: '5px', 
                      fontSize: '14px'
                    }}
                  />
                  {errors.mobileNumber && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.mobileNumber}</p>}
                </div>

                {/* Email */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Email Address *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      Unique verified address (FATF Rec.10)
                    </span>
                  </label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange}
                    placeholder="email@example.com" 
                    style={{
                      width: '100%', 
                      padding: '10px',
                      border: errors.email ? '2px solid #f44336' : '1px solid #ccc',
                      borderRadius: '5px', 
                      fontSize: '14px'
                    }}
                  />
                  {errors.email && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.email}</p>}
                </div>

                {/* Proof of Address Upload */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Proof of Address *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      Accept utility bill / eStatement ≤ 3 months old (CBUAE Art.8)
                    </span>
                  </label>
                  
                  {!formData.documents.proofOfAddress ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'proofOfAddress')}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: errors.proofOfAddress ? '2px solid #f44336' : '1px solid #ccc', 
                          borderRadius: '5px', 
                          fontSize: '14px' 
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Max 5MB • JPG, PNG, PDF • Must be dated within last 3 months
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      padding: '10px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '5px' 
                    }}>
                      <span style={{ flex: 1, fontSize: '14px' }}>
                        ✓ {formData.documents.proofOfAddress.name} ({(formData.documents.proofOfAddress.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile('proofOfAddress')}
                        style={{ 
                          backgroundColor: '#f44336', 
                          color: 'white', 
                          border: 'none', 
                          padding: '6px 12px', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px' 
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {errors.proofOfAddress && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.proofOfAddress}</p>}
                </div>
              </div>
            )}

            {/* CARD 3 - ID DOCUMENTS */}
            {currentStep === 3 && (
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ 
                  color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                  borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
                }}>
                  CARD 3 – ID Documents
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                    (CBUAE Art.3, FATF Rec.10)
                  </span>
                </h3>

                <div style={{
                  backgroundColor: '#fff3e0',
                  padding: '15px',
                  borderRadius: '5px',
                  marginBottom: '20px',
                  borderLeft: '4px solid #ff9800'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#e65100' }}>
                    <strong>📌 Important:</strong> Upload clear, readable documents. All documents must be valid and not expired. 
                    OCR validation will be performed automatically.
                  </p>
                </div>

                {/* Emirates ID Front */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Emirates ID (Front) *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      OCR + checksum validation (CBUAE Art.3)
                    </span>
                  </label>
                  
                  {!formData.documents.emiratesIdFront ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'emiratesIdFront')}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: errors.emiratesIdFront ? '2px solid #f44336' : '1px solid #ccc', 
                          borderRadius: '5px', 
                          fontSize: '14px' 
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Max 5MB • JPG, PNG, PDF • Front side of Emirates ID
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      padding: '10px', 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '5px',
                      border: '2px solid #4caf50'
                    }}>
                      <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                        ✓ {formData.documents.emiratesIdFront.name} ({(formData.documents.emiratesIdFront.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile('emiratesIdFront')}
                        style={{ 
                          backgroundColor: '#f44336', 
                          color: 'white', 
                          border: 'none', 
                          padding: '6px 12px', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px' 
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {errors.emiratesIdFront && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.emiratesIdFront}</p>}
                </div>

                {/* Emirates ID Back */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Emirates ID (Back) *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      CBUAE Art.3 requirement
                    </span>
                  </label>
                  
                  {!formData.documents.emiratesIdBack ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'emiratesIdBack')}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: errors.emiratesIdBack ? '2px solid #f44336' : '1px solid #ccc', 
                          borderRadius: '5px', 
                          fontSize: '14px' 
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Max 5MB • JPG, PNG, PDF • Back side of Emirates ID
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      padding: '10px', 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '5px',
                      border: '2px solid #4caf50'
                    }}>
                      <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                        ✓ {formData.documents.emiratesIdBack.name} ({(formData.documents.emiratesIdBack.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile('emiratesIdBack')}
                        style={{ 
                          backgroundColor: '#f44336', 
                          color: 'white', 
                          border: 'none', 
                          padding: '6px 12px', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px' 
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {errors.emiratesIdBack && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.emiratesIdBack}</p>}
                </div>

                {/* Passport (Optional for UAE nationals) */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Passport {formData.nationality !== 'AE' && '*'}
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      {formData.nationality === 'AE' ? 'Optional for UAE nationals' : 'Required for non-UAE nationals'} • OCR + expiry validation (FATF Rec.10)
                    </span>
                  </label>
                  
                  {!formData.documents.passport ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'passport')}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: errors.passport ? '2px solid #f44336' : '1px solid #ccc', 
                          borderRadius: '5px', 
                          fontSize: '14px' 
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Max 5MB • JPG, PNG, PDF • Passport bio-data page
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      padding: '10px', 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '5px',
                      border: '2px solid #4caf50'
                    }}>
                      <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                        ✓ {formData.documents.passport.name} ({(formData.documents.passport.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile('passport')}
                        style={{ 
                          backgroundColor: '#f44336', 
                          color: 'white', 
                          border: 'none', 
                          padding: '6px 12px', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px' 
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {errors.passport && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.passport}</p>}
                </div>

                {/* Visa / Residence Permit (for expats) */}
                {formData.nationality !== 'AE' && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Visa / Residence Permit
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        For expats • OCR + expiry validation (FATF Rec.10)
                      </span>
                    </label>
                    
                    {!formData.documents.visa ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'visa')}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: errors.visa ? '2px solid #f44336' : '1px solid #ccc', 
                            borderRadius: '5px', 
                            fontSize: '14px' 
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Max 5MB • JPG, PNG, PDF • Valid visa/residence permit
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '5px',
                        border: '2px solid #4caf50'
                      }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                          ✓ {formData.documents.visa.name} ({(formData.documents.visa.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile('visa')}
                          style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '12px' 
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {errors.visa && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.visa}</p>}
                  </div>
                )}

                {/* Signature Specimen */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Signature Specimen *
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                      Match with documents; digital signature hash optional (CBUAE Art.8)
                    </span>
                  </label>
                  
                  {!formData.documents.signatureSpecimen ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'signatureSpecimen')}
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: errors.signatureSpecimen ? '2px solid #f44336' : '1px solid #ccc', 
                          borderRadius: '5px', 
                          fontSize: '14px' 
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Max 5MB • JPG, PNG, PDF • Clear signature on white paper or digital signature
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      padding: '10px', 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '5px',
                      border: '2px solid #4caf50'
                    }}>
                      <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                        ✓ {formData.documents.signatureSpecimen.name} ({(formData.documents.signatureSpecimen.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile('signatureSpecimen')}
                        style={{ 
                          backgroundColor: '#f44336', 
                          color: 'white', 
                          border: 'none', 
                          padding: '6px 12px', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px' 
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {errors.signatureSpecimen && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.signatureSpecimen}</p>}
                </div>

                {/* Document Upload Summary */}
                <div style={{
                  backgroundColor: '#e3f2fd',
                  padding: '15px',
                  borderRadius: '5px',
                  marginTop: '20px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#0d47a1', fontSize: '14px' }}>
                    📋 Document Upload Summary
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                    <div>
                      <span style={{ color: formData.documents.emiratesIdFront ? '#4caf50' : '#999' }}>
                        {formData.documents.emiratesIdFront ? '✓' : '○'} Emirates ID (Front)
                      </span>
                    </div>
                    <div>
                      <span style={{ color: formData.documents.emiratesIdBack ? '#4caf50' : '#999' }}>
                        {formData.documents.emiratesIdBack ? '✓' : '○'} Emirates ID (Back)
                      </span>
                    </div>
                    <div>
                      <span style={{ color: formData.documents.passport ? '#4caf50' : '#999' }}>
                        {formData.documents.passport ? '✓' : '○'} Passport
                      </span>
                    </div>
                    <div>
                      <span style={{ color: formData.documents.visa ? '#4caf50' : '#999' }}>
                        {formData.documents.visa ? '✓' : '○'} Visa/Residence Permit
                      </span>
                    </div>
                    <div>
                      <span style={{ color: formData.documents.signatureSpecimen ? '#4caf50' : '#999' }}>
                        {formData.documents.signatureSpecimen ? '✓' : '○'} Signature Specimen
                      </span>
                    </div>
                    <div>
                      <span style={{ color: formData.documents.proofOfAddress ? '#4caf50' : '#999' }}>
                        {formData.documents.proofOfAddress ? '✓' : '○'} Proof of Address
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* CARD 4 - SOURCE OF FUNDS / SOURCE OF WEALTH */}
          {currentStep === 4 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 4 – Source of Funds / Source of Wealth
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.3, Art.16, FATF Rec.10, Rec.24)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#fff3e0',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '25px',
                borderLeft: '4px solid #ff9800'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#e65100' }}>
                  <strong>📌 Important:</strong> Provide detailed information about your source of funds and wealth. 
                  This helps us comply with UAE AML regulations and complete your risk assessment.
                </p>
              </div>

              {/* PRIMARY SOURCE OF FUNDS SELECTOR */}
              <div style={{ marginBottom: '25px', backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '5px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px', fontSize: '15px' }}>
                  Primary Source of Funds *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Select your main source of income (FATF Rec.10)
                  </span>
                </label>
                <select 
                  name="sourceOfFunds" 
                  value={formData.sourceOfFunds} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '12px',
                    border: errors.sourceOfFunds ? '2px solid #f44336' : '2px solid #1976d2',
                    borderRadius: '5px', 
                    fontSize: '14px',
                    fontWeight: 'bold',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">-- Select Primary Source --</option>
                  <option value="Employment">Employment Income</option>
                  <option value="Business">Business Income</option>
                  <option value="Investment">Investment Returns</option>
                  <option value="Inheritance">Inheritance</option>
                  <option value="Gift">Gift</option>
                  <option value="Property">Property Sale</option>
                  <option value="Savings">Personal Savings</option>
                  <option value="Other">Other</option>
                </select>
                {errors.sourceOfFunds && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.sourceOfFunds}</p>}
              </div>

              {/* ============================================ */}
              {/* SECTION A: EMPLOYMENT INCOME */}
              {/* ============================================ */}
              {(formData.sourceOfFunds === 'Employment' || formData.sourceOfFunds === 'Savings') && (
                <div style={{ 
                  marginBottom: '30px', 
                  padding: '20px', 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: '8px',
                  border: '2px solid #1976d2'
                }}>
                  <h4 style={{ 
                    color: '#0d47a1', 
                    fontSize: '16px', 
                    marginBottom: '15px',
                    borderBottom: '2px solid #0d47a1',
                    paddingBottom: '8px'
                  }}>
                    📊 Employment Income Details
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                      (CBUAE Art.3, FATF Rec.10)
                    </span>
                  </h4>

                  {/* Employer Name */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Employer Name *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Verify legitimacy and sanctions list
                      </span>
                    </label>
                    <input 
                      type="text" 
                      name="employerName" 
                      value={formData.employerName} 
                      onChange={handleChange}
                      placeholder="Enter current employer name" 
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.employerName ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.employerName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.employerName}</p>}
                  </div>

                  {/* Employer Address */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Employer Address
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Capture for cross-border risk (FATF Rec.10)
                      </span>
                    </label>
                    <input 
                      type="text" 
                      name="employerAddress" 
                      value={formData.employerAddress} 
                      onChange={handleChange}
                      placeholder="Enter employer's office address" 
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.employerAddress ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.employerAddress && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.employerAddress}</p>}
                  </div>

                  {/* Job Title / Designation */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Job Title / Designation *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Align with occupation field (FATF Rec.10)
                      </span>
                    </label>
                    <input 
                      type="text" 
                      name="jobTitle" 
                      value={formData.jobTitle} 
                      onChange={handleChange}
                      placeholder="e.g., Senior Engineer, Manager, Accountant" 
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.jobTitle ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.jobTitle && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.jobTitle}</p>}
                  </div>

                  {/* Employment Type */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Employment Type *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Full-time / Part-time / Contract (FATF Rec.10)
                      </span>
                    </label>
                    <select 
                      name="employmentType" 
                      value={formData.employmentType} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.employmentType ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select employment type</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Freelance">Freelance</option>
                      <option value="Self-Employed">Self-Employed</option>
                    </select>
                    {errors.employmentType && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.employmentType}</p>}
                  </div>

                  {/* Monthly Salary */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Monthly Salary (AED) *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Compare with declared income range (CBUAE Art.16)
                      </span>
                    </label>
                    <input 
                      type="number" 
                      name="monthlySalary" 
                      value={formData.monthlySalary} 
                      onChange={handleChange}
                      placeholder="e.g., 15000" 
                      min="0"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.monthlySalary ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.monthlySalary && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.monthlySalary}</p>}
                  </div>

                  {/* Mode of Payment */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Mode of Payment
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Bank / Cash / Cheque (FATF Rec.10)
                      </span>
                    </label>
                    <select 
                      name="paymentMode" 
                      value={formData.paymentMode} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.paymentMode ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select payment mode</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.paymentMode && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.paymentMode}</p>}
                  </div>

                  {/* Duration of Employment */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Duration of Employment (Years)
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        ≥ 1 year reduces risk (FATF Rec.10)
                      </span>
                    </label>
                    <input 
                      type="number" 
                      name="employmentDuration" 
                      value={formData.employmentDuration} 
                      onChange={handleChange}
                      placeholder="e.g., 3" 
                      min="0"
                      step="0.5"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.employmentDuration ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.employmentDuration && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.employmentDuration}</p>}
                  </div>

                  {/* Proof of Employment */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Proof of Employment
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Upload salary slip or employment letter ≤3 months (FATF Rec.10)
                      </span>
                    </label>
                    
                    {!formData.documents.proofOfEmployment ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'proofOfEmployment')}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: errors.proofOfEmployment ? '2px solid #f44336' : '1px solid #ccc', 
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Max 5MB • JPG, PNG, PDF • Recent salary slip or employment letter
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '5px',
                        border: '2px solid #4caf50'
                      }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                          ✓ {formData.documents.proofOfEmployment.name} ({(formData.documents.proofOfEmployment.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile('proofOfEmployment')}
                          style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '12px' 
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {errors.proofOfEmployment && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.proofOfEmployment}</p>}
                  </div>

                  {/* Bank Statement */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Bank Statement
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Validate salary credits (CBUAE Art.8)
                      </span>
                    </label>
                    
                    {!formData.documents.bankStatement ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'bankStatement')}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: errors.bankStatement ? '2px solid #f44336' : '1px solid #ccc', 
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Max 5MB • JPG, PNG, PDF • Last 3 months bank statement
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '5px',
                        border: '2px solid #4caf50'
                      }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                          ✓ {formData.documents.bankStatement.name} ({(formData.documents.bankStatement.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile('bankStatement')}
                          style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '12px' 
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {errors.bankStatement && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.bankStatement}</p>}
                  </div>
                </div>
              )}

              {/* ============================================ */}
              {/* SECTION B: BUSINESS INCOME */}
              {/* ============================================ */}
              {formData.sourceOfFunds === 'Business' && (
                <div style={{ 
                  marginBottom: '30px', 
                  padding: '20px', 
                  backgroundColor: '#f3e5f5', 
                  borderRadius: '8px',
                  border: '2px solid #9c27b0'
                }}>
                  <h4 style={{ 
                    color: '#6a1b9a', 
                    fontSize: '16px', 
                    marginBottom: '15px',
                    borderBottom: '2px solid #9c27b0',
                    paddingBottom: '8px'
                  }}>
                    🏢 Business Income Details
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                      (CBUAE Art.4, FATF Rec.24, MoE DNFBP Rulebook)
                    </span>
                  </h4>

                  {/* Business / Trade Name */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Business / Trade Name *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Match trade license (CBUAE Art.4)
                      </span>
                    </label>
                    <input 
                      type="text" 
                      name="businessName" 
                      value={formData.businessName} 
                      onChange={handleChange}
                      placeholder="Enter registered business name" 
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.businessName ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.businessName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.businessName}</p>}
                  </div>

                  {/* License / Registration No. */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      License / Registration Number *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        OCR validation, expiry check (CBUAE Art.4)
                      </span>
                    </label>
                    <input 
                      type="text" 
                      name="licenseNumber" 
                      value={formData.licenseNumber} 
                      onChange={handleChange}
                      placeholder="Enter trade license number" 
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.licenseNumber ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.licenseNumber && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.licenseNumber}</p>}
                  </div>

                  {/* License Issuing Authority */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      License Issuing Authority *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        DED / DMCC / DIFC etc. (MoE DNFBP Rulebook)
                      </span>
                    </label>
                    <select 
                      name="licenseAuthority" 
                      value={formData.licenseAuthority} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.licenseAuthority ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select issuing authority</option>
                      <option value="DED">Department of Economic Development (DED)</option>
                      <option value="DMCC">Dubai Multi Commodities Centre (DMCC)</option>
                      <option value="DIFC">Dubai International Financial Centre (DIFC)</option>
                      <option value="ADGM">Abu Dhabi Global Market (ADGM)</option>
                      <option value="JAFZA">Jebel Ali Free Zone (JAFZA)</option>
                      <option value="RAK">Ras Al Khaimah Economic Zone</option>
                      <option value="Sharjah">Sharjah Economic Development</option>
                      <option value="Other">Other Free Zone</option>
                    </select>
                    {errors.licenseAuthority && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.licenseAuthority}</p>}
                  </div>

                  {/* Nature of Business */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Nature of Business *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Select from taxonomy (for risk classification) (FATF Rec.10)
                      </span>
                    </label>
                    <select 
                      name="businessNature" 
                      value={formData.businessNature} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.businessNature ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select business nature</option>
                      <option value="Trading">Trading / Import-Export</option>
                      <option value="Retail">Retail / E-commerce</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Construction">Construction / Real Estate Development</option>
                      <option value="Services">Professional Services</option>
                      <option value="Hospitality">Hospitality / Tourism</option>
                      <option value="Technology">Technology / IT Services</option>
                      <option value="Financial">Financial Services</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Education">Education</option>
                      <option value="Transportation">Transportation / Logistics</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.businessNature && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.businessNature}</p>}
                  </div>

                  {/* Years in Operation */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Years in Operation
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        ≥ 1 year preferred (used in risk score) (FATF Rec.10)
                      </span>
                    </label>
                    <input 
                      type="number" 
                      name="yearsInOperation" 
                      value={formData.yearsInOperation} 
                      onChange={handleChange}
                      placeholder="e.g., 5" 
                      min="0"
                      step="0.5"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.yearsInOperation ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.yearsInOperation && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.yearsInOperation}</p>}
                  </div>

                  {/* Average Monthly Turnover */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Average Monthly Turnover (AED)
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Used for risk score (CBUAE Art.16)
                      </span>
                    </label>
                    <input 
                      type="number" 
                      name="monthlyTurnover" 
                      value={formData.monthlyTurnover} 
                      onChange={handleChange}
                      placeholder="e.g., 50000" 
                      min="0"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.monthlyTurnover ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.monthlyTurnover && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.monthlyTurnover}</p>}
                  </div>

                  {/* Primary Bank Name */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Primary Bank Name
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Validate bank name (FATF Rec.10)
                      </span>
                    </label>
                    <input 
                      type="text" 
                      name="primaryBankName" 
                      value={formData.primaryBankName} 
                      onChange={handleChange}
                      placeholder="e.g., Emirates NBD, ADCB, Mashreq" 
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.primaryBankName ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.primaryBankName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.primaryBankName}</p>}
                  </div>

                  {/* Bank Account Country */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Bank Account Country
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Jurisdiction risk scoring (FATF Rec.10)
                      </span>
                    </label>
                    <select 
                      name="bankAccountCountry" 
                      value={formData.bankAccountCountry} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.bankAccountCountry ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select country</option>
                      <option value="AE">United Arab Emirates</option>
                      <option value="SA">Saudi Arabia</option>
                      <option value="KW">Kuwait</option>
                      <option value="QA">Qatar</option>
                      <option value="BH">Bahrain</option>
                      <option value="OM">Oman</option>
                      <option value="IN">India</option>
                      <option value="PK">Pakistan</option>
                      <option value="US">United States</option>
                      <option value="GB">United Kingdom</option>
                      <option value="OTHER">Other</option>
                    </select>
                    {errors.bankAccountCountry && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.bankAccountCountry}</p>}
                  </div>

                  {/* Trade License Upload */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Trade License *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Valid trade license copy (CBUAE Art.4)
                      </span>
                    </label>
                    
                    {!formData.documents.tradeLicense ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'tradeLicense')}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: errors.tradeLicense ? '2px solid #f44336' : '1px solid #ccc', 
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Max 5MB • JPG, PNG, PDF • Valid trade license
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '5px',
                        border: '2px solid #4caf50'
                      }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                          ✓ {formData.documents.tradeLicense.name} ({(formData.documents.tradeLicense.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile('tradeLicense')}
                          style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '12px' 
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {errors.tradeLicense && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.tradeLicense}</p>}
                  </div>

                  {/* Proof of Business Income */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Proof of Business Income
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Invoices / statements ≥3 months (FATF Rec.10)
                      </span>
                    </label>
                    
                    {!formData.documents.businessIncome ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'businessIncome')}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: errors.businessIncome ? '2px solid #f44336' : '1px solid #ccc', 
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Max 5MB • JPG, PNG, PDF • Business bank statements or invoices
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '5px',
                        border: '2px solid #4caf50'
                      }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                          ✓ {formData.documents.businessIncome.name} ({(formData.documents.businessIncome.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile('businessIncome')}
                          style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '12px' 
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {errors.businessIncome && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.businessIncome}</p>}
                  </div>

                  {/* VAT Certificate (Optional) */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      VAT Certificate (Optional)
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Optional for SMBs (MoE Rulebook)
                      </span>
                    </label>
                    
                    {!formData.documents.vatCertificate ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(e, 'vatCertificate')}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: errors.vatCertificate ? '2px solid #f44336' : '1px solid #ccc', 
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          Max 5MB • JPG, PNG, PDF • VAT registration certificate
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '10px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '5px',
                        border: '2px solid #4caf50'
                      }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                          ✓ {formData.documents.vatCertificate.name} ({(formData.documents.vatCertificate.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile('vatCertificate')}
                          style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '12px' 
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {errors.vatCertificate && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.vatCertificate}</p>}
                  </div>
                </div>
              )}

              {/* ============================================ */}
              {/* SECTION C: OTHER SOURCES OF WEALTH */}
              {/* ============================================ */}
              {(formData.sourceOfFunds === 'Inheritance' || 
                formData.sourceOfFunds === 'Gift' || 
                formData.sourceOfFunds === 'Property' || 
                formData.sourceOfFunds === 'Investment' ||
                formData.sourceOfFunds === 'Other') && (
                <div style={{ 
                  marginBottom: '30px', 
                  padding: '20px', 
                  backgroundColor: '#fff8e1', 
                  borderRadius: '8px',
                  border: '2px solid #fbc02d'
                }}>
                  <h4 style={{ 
                    color: '#f57f17', 
                    fontSize: '16px', 
                    marginBottom: '15px',
                    borderBottom: '2px solid #fbc02d',
                    paddingBottom: '8px'
                  }}>
                    💎 Other Sources of Wealth
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                      (FATF Rec.10, CBUAE Art.16)
                    </span>
                  </h4>

                  {/* Wealth Source Type */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Wealth Source Type *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Select: Inheritance / Gift / Property / Investment (FATF Rec.10)
                      </span>
                    </label>
                    <select 
                      name="wealthSourceType" 
                      value={formData.wealthSourceType} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.wealthSourceType ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select wealth source type</option>
                      <option value="Inheritance">Inheritance</option>
                      <option value="Gift">Gift / Donation</option>
                      <option value="Property">Property Sale</option>
                      <option value="Investment">Investment Returns</option>
                      <option value="Savings">Long-term Savings</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.wealthSourceType && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.wealthSourceType}</p>}
                  </div>

                  {/* Description of Source */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Description of Source *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Explanation in your own words (FATF Rec.10)
                      </span>
                    </label>
                    <textarea 
                      name="wealthDescription" 
                      value={formData.wealthDescription} 
                      onChange={handleChange}
                      placeholder="Please provide detailed explanation of the source of wealth" 
                      rows="4"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.wealthDescription ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white',
                        resize: 'vertical'
                      }}
                    />
                    {errors.wealthDescription && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.wealthDescription}</p>}
                  </div>

                  {/* Date of Receipt */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Date of Receipt
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        For recency validation (FATF Rec.10)
                      </span>
                    </label>
                    <input 
                      type="date" 
                      name="wealthReceiptDate" 
                      value={formData.wealthReceiptDate} 
                      onChange={handleChange}
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.wealthReceiptDate ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.wealthReceiptDate && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.wealthReceiptDate}</p>}
                  </div>

                  {/* Approximate Value */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                      Approximate Value (AED)
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Used in SoW scoring (CBUAE Art.16)
                      </span>
                    </label>
                    <input 
                      type="number" 
                      name="wealthValue" 
                      value={formData.wealthValue} 
                      onChange={handleChange}
                      placeholder="e.g., 500000" 
                      min="0"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.wealthValue ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                    {errors.wealthValue && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.wealthValue}</p>}
                  </div>

                  {/* Gift/Inheritance specific fields */}
                  {(formData.wealthSourceType === 'Gift' || formData.wealthSourceType === 'Inheritance') && (
                    <>
                      {/* Giver / Donor Name */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                          {formData.wealthSourceType === 'Gift' ? 'Giver / Donor Name' : 'Deceased Name'}
                          <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                            Trace chain of funds (FATF Rec.10)
                          </span>
                        </label>
                        <input 
                          type="text" 
                          name="giverName" 
                          value={formData.giverName} 
                          onChange={handleChange}
                          placeholder={formData.wealthSourceType === 'Gift' ? "Enter giver's name" : "Enter deceased's name"} 
                          style={{
                            width: '100%', 
                            padding: '10px',
                            border: errors.giverName ? '2px solid #f44336' : '1px solid #ccc',
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                        {errors.giverName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.giverName}</p>}
                      </div>

                      {/* Relationship to Giver */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                          Relationship to {formData.wealthSourceType === 'Gift' ? 'Giver' : 'Deceased'}
                          <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                            Family / Associate / Other (FATF Rec.10)
                          </span>
                        </label>
                        <select 
                          name="relationshipToGiver" 
                          value={formData.relationshipToGiver} 
                          onChange={handleChange}
                          style={{
                            width: '100%', 
                            padding: '10px',
                            border: errors.relationshipToGiver ? '2px solid #f44336' : '1px solid #ccc',
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value="">Select relationship</option>
                          <option value="Parent">Parent</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Child">Child</option>
                          <option value="Grandparent">Grandparent</option>
                          <option value="Uncle/Aunt">Uncle / Aunt</option>
                          <option value="Other Relative">Other Relative</option>
                          <option value="Friend">Friend</option>
                          <option value="Business Partner">Business Partner</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.relationshipToGiver && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.relationshipToGiver}</p>}
                      </div>

                      {/* Inheritance Documentation */}
                      {formData.wealthSourceType === 'Inheritance' && (
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                            Inheritance Documentation
                            <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                              Will / certificate of inheritance (FATF Rec.10)
                            </span>
                          </label>
                          
                          {!formData.documents.inheritanceDoc ? (
                            <div>
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={(e) => handleFileChange(e, 'inheritanceDoc')}
                                style={{ 
                                  width: '100%', 
                                  padding: '10px', 
                                  border: errors.inheritanceDoc ? '2px solid #f44336' : '1px solid #ccc', 
                                  borderRadius: '5px', 
                                  fontSize: '14px',
                                  backgroundColor: 'white'
                                }}
                              />
                              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Max 5MB • JPG, PNG, PDF • Will or inheritance certificate
                              </p>
                            </div>
                          ) : (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px', 
                              padding: '10px', 
                              backgroundColor: '#e8f5e9', 
                              borderRadius: '5px',
                              border: '2px solid #4caf50'
                            }}>
                              <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                                ✓ {formData.documents.inheritanceDoc.name} ({(formData.documents.inheritanceDoc.size / 1024).toFixed(1)} KB)
                              </span>
                              <button
                                type="button"
                                onClick={() => removeFile('inheritanceDoc')}
                                style={{ 
                                  backgroundColor: '#f44336', 
                                  color: 'white', 
                                  border: 'none', 
                                  padding: '6px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer', 
                                  fontSize: '12px' 
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                          {errors.inheritanceDoc && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.inheritanceDoc}</p>}
                        </div>
                      )}
                    </>
                  )}

                  {/* Property Sale specific fields */}
                  {formData.wealthSourceType === 'Property' && (
                    <>
                      {/* Property / Asset Description */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                          Property / Asset Description
                          <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                            Include details of sold asset (FATF Rec.10)
                          </span>
                        </label>
                        <textarea 
                          name="propertyDescription" 
                          value={formData.propertyDescription} 
                          onChange={handleChange}
                          placeholder="Describe the property or asset that was sold" 
                          rows="3"
                          style={{
                            width: '100%', 
                            padding: '10px',
                            border: errors.propertyDescription ? '2px solid #f44336' : '1px solid #ccc',
                            borderRadius: '5px', 
                            fontSize: '14px',
                            backgroundColor: 'white',
                            resize: 'vertical'
                          }}
                        />
                        {errors.propertyDescription && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.propertyDescription}</p>}
                      </div>

                      {/* Property Documentation */}
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                          Proof of Ownership / Transfer
                          <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                            Title deed / transfer evidence (CBUAE Art.8)
                          </span>
                        </label>
                        
                        {!formData.documents.propertyDoc ? (
                          <div>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileChange(e, 'propertyDoc')}
                              style={{ 
                                width: '100%', 
                                padding: '10px', 
                                border: errors.propertyDoc ? '2px solid #f44336' : '1px solid #ccc', 
                                borderRadius: '5px', 
                                fontSize: '14px',
                                backgroundColor: 'white'
                              }}
                            />
                            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                              Max 5MB • JPG, PNG, PDF • Sale deed or transfer document
                            </p>
                          </div>
                        ) : (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            padding: '10px', 
                            backgroundColor: '#e8f5e9', 
                            borderRadius: '5px',
                            border: '2px solid #4caf50'
                          }}>
                            <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                              ✓ {formData.documents.propertyDoc.name} ({(formData.documents.propertyDoc.size / 1024).toFixed(1)} KB)
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile('propertyDoc')}
                              style={{ 
                                backgroundColor: '#f44336', 
                                color: 'white', 
                                border: 'none', 
                                padding: '6px 12px', 
                                borderRadius: '4px', 
                                cursor: 'pointer', 
                                fontSize: '12px' 
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {errors.propertyDoc && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.propertyDoc}</p>}
                      </div>
                    </>
                  )}

                  {/* Investment specific fields */}
                  {formData.wealthSourceType === 'Investment' && (
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                        Investment Portfolio Statements
                        <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                          Validate against source institution (FATF Rec.10)
                        </span>
                      </label>
                      
                      {!formData.documents.investmentStatement ? (
                        <div>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileChange(e, 'investmentStatement')}
                            style={{ 
                              width: '100%', 
                              padding: '10px', 
                              border: errors.investmentStatement ? '2px solid #f44336' : '1px solid #ccc', 
                              borderRadius: '5px', 
                              fontSize: '14px',
                              backgroundColor: 'white'
                            }}
                          />
                          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            Max 5MB • JPG, PNG, PDF • Investment statements or portfolio summary
                          </p>
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px', 
                          padding: '10px', 
                          backgroundColor: '#e8f5e9', 
                          borderRadius: '5px',
                          border: '2px solid #4caf50'
                        }}>
                          <span style={{ flex: 1, fontSize: '14px', color: '#2e7d32' }}>
                            ✓ {formData.documents.investmentStatement.name} ({(formData.documents.investmentStatement.size / 1024).toFixed(1)} KB)
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile('investmentStatement')}
                            style={{ 
                              backgroundColor: '#f44336', 
                              color: 'white', 
                              border: 'none', 
                              padding: '6px 12px', 
                              borderRadius: '4px', 
                              cursor: 'pointer', 
                              fontSize: '12px' 
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      {errors.investmentStatement && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.investmentStatement}</p>}
                    </div>
                  )}
                </div>
              )}

              {/* Source of Funds Summary */}
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '20px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#0d47a1', fontSize: '14px' }}>
                  📊 Source of Funds Summary
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Primary Source:</strong> {formData.sourceOfFunds || 'Not selected'}
                  </p>
                  {formData.sourceOfFunds === 'Employment' && formData.employerName && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>Employer:</strong> {formData.employerName}
                      {formData.monthlySalary && ` • Salary: AED ${formData.monthlySalary}`}
                    </p>
                  )}
                  {formData.sourceOfFunds === 'Business' && formData.businessName && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>Business:</strong> {formData.businessName}
                      {formData.licenseNumber && ` • License: ${formData.licenseNumber}`}
                    </p>
                  )}
                  {(formData.sourceOfFunds === 'Inheritance' || formData.sourceOfFunds === 'Gift') && formData.wealthSourceType && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>Wealth Type:</strong> {formData.wealthSourceType}
                      {formData.wealthValue && ` • Value: AED ${formData.wealthValue}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* CARD 5 - EXPECTED ACTIVITY */}
          {currentStep === 5 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 5 – Expected Activity
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.16, FATF Rec.10)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#e8f5e9',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '25px',
                borderLeft: '4px solid #4caf50'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#2e7d32' }}>
                  <strong>ℹ️ Information:</strong> Help us understand your expected transaction patterns and business relationships. 
                  This information is used for transaction monitoring and risk assessment.
                </p>
              </div>

              {/* Purpose of Relationship */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Purpose of Relationship *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Why are you establishing this business relationship? (UAE AML Law Art.4)
                  </span>
                </label>
                <select 
                  name="purposeOfRelationship" 
                  value={formData.purposeOfRelationship} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.purposeOfRelationship ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select purpose</option>
                  <option value="Real Estate Transaction">Real Estate Transaction</option>
                  <option value="Real Estate Advisory">Real Estate Advisory</option>
                  <option value="Property Management">Property Management</option>
                  <option value="Investment Advisory">Investment Advisory</option>
                  <option value="Financial Planning">Financial Planning</option>
                  <option value="Business Consultation">Business Consultation</option>
                  <option value="Legal Services">Legal Services</option>
                  <option value="Accounting Services">Accounting Services</option>
                  <option value="Trust Services">Trust Services</option>
                  <option value="Company Formation">Company Formation</option>
                  <option value="Other">Other Professional Services</option>
                </select>
                {errors.purposeOfRelationship && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.purposeOfRelationship}</p>}
              </div>

              {/* Expected Transaction Volume */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Expected Transaction Volume (AED) *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Estimated total value of transactions per year (CBUAE Art.16)
                  </span>
                </label>
                <select 
                  name="expectedTransactionVolume" 
                  value={formData.expectedTransactionVolume} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.expectedTransactionVolume ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select expected volume</option>
                  <option value="0-50000">AED 0 - 50,000</option>
                  <option value="50000-100000">AED 50,000 - 100,000</option>
                  <option value="100000-250000">AED 100,000 - 250,000</option>
                  <option value="250000-500000">AED 250,000 - 500,000</option>
                  <option value="500000-1000000">AED 500,000 - 1,000,000</option>
                  <option value="1000000-5000000">AED 1,000,000 - 5,000,000</option>
                  <option value="5000000+">AED 5,000,000+</option>
                </select>
                {errors.expectedTransactionVolume && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.expectedTransactionVolume}</p>}
              </div>

              {/* Expected Transaction Frequency */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Expected Transaction Frequency *
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    How often do you expect to conduct transactions? (CBUAE Art.16)
                  </span>
                </label>
                <select 
                  name="expectedTransactionFrequency" 
                  value={formData.expectedTransactionFrequency} 
                  onChange={handleChange}
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.expectedTransactionFrequency ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select frequency</option>
                  <option value="One-time">One-time Transaction</option>
                  <option value="Occasional">Occasional (1-3 times per year)</option>
                  <option value="Quarterly">Quarterly (4 times per year)</option>
                  <option value="Monthly">Monthly (12 times per year)</option>
                  <option value="Weekly">Weekly (50+ times per year)</option>
                  <option value="Daily">Daily / High Frequency</option>
                </select>
                {errors.expectedTransactionFrequency && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.expectedTransactionFrequency}</p>}
              </div>

              {/* Expected Counterparties / Countries */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Expected Counterparties / Countries
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    Which countries/jurisdictions will you be transacting with? (FATF Rec.10)
                  </span>
                </label>
                <textarea 
                  name="expectedCounterparties" 
                  value={formData.expectedCounterparties} 
                  onChange={handleChange}
                  placeholder="e.g., UAE, Saudi Arabia, India, UK" 
                  rows="3"
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.expectedCounterparties ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                {errors.expectedCounterparties && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.expectedCounterparties}</p>}
              </div>

              {/* Anticipated Services */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Anticipated Services / Products
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                    What specific services or products are you interested in? (CBUAE Art.16)
                  </span>
                </label>
                <textarea 
                  name="anticipatedServices" 
                  value={formData.anticipatedServices} 
                  onChange={handleChange}
                  placeholder="Describe the services or products you plan to use" 
                  rows="4"
                  style={{
                    width: '100%', 
                    padding: '10px',
                    border: errors.anticipatedServices ? '2px solid #f44336' : '1px solid #ccc',
                    borderRadius: '5px', 
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                {errors.anticipatedServices && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.anticipatedServices}</p>}
              </div>

              {/* Expected Activity Summary */}
              <div style={{
                backgroundColor: '#fff3e0',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '20px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#e65100', fontSize: '14px' }}>
                  📊 Expected Activity Summary
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Purpose:</strong> {formData.purposeOfRelationship || 'Not specified'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Transaction Volume:</strong> {formData.expectedTransactionVolume || 'Not specified'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Transaction Frequency:</strong> {formData.expectedTransactionFrequency || 'Not specified'}
                  </p>
                </div>
                <p style={{ fontSize: '11px', color: '#666', marginTop: '10px', marginBottom: 0 }}>
                  This information helps us provide better service and comply with AML/CFT regulations.
                </p>
              </div>
            </div>
          )}
          
          {/* CARD 6 - SANCTIONS/PEP SCREENING & CONSENT */}
          {currentStep === 6 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 6 – Sanctions / PEP Screening & Consent
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (FATF Rec.12, CBUAE Art.9, UAE DP Law Art.40)
                </span>
              </h3>

              {/* ============================================ */}
              {/* SECTION A: PEP DECLARATION */}
              {/* ============================================ */}
              <div style={{ 
                marginBottom: '30px', 
                padding: '20px', 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px',
                border: '2px solid #ff9800'
              }}>
                <h4 style={{ 
                  color: '#e65100', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #ff9800',
                  paddingBottom: '8px'
                }}>
                  🔍 Politically Exposed Person (PEP) Declaration
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                    (FATF Rec.12, CBUAE Art.9)
                  </span>
                </h4>

                <div style={{
                  backgroundColor: '#fffbf0',
                  padding: '15px',
                  borderRadius: '5px',
                  marginBottom: '20px',
                  borderLeft: '4px solid #ff9800'
                }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 'bold', color: '#e65100' }}>
                    What is a Politically Exposed Person (PEP)?
                  </p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666', lineHeight: '1.6' }}>
                    A PEP is an individual who holds or has held a prominent public function, such as:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#666', lineHeight: '1.6' }}>
                    <li>Heads of state or government, ministers, senior politicians</li>
                    <li>Senior government officials, judicial or military officers</li>
                    <li>Senior executives of state-owned corporations</li>
                    <li>Important political party officials</li>
                    <li><strong>Family members</strong> (spouse, children, parents) of the above</li>
                    <li><strong>Close associates</strong> known to have joint beneficial ownership or close business relationships</li>
                  </ul>
                </div>

                {/* PEP Status Checkbox */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'start', 
                    padding: '15px',
                    backgroundColor: 'white', 
                    borderRadius: '5px', 
                    cursor: 'pointer',
                    border: formData.pepStatus ? '2px solid #ff9800' : '1px solid #ccc'
                  }}>
                    <input 
                      type="checkbox" 
                      name="pepStatus" 
                      checked={formData.pepStatus} 
                      onChange={handleChange}
                      style={{ width: '20px', height: '20px', marginRight: '12px', marginTop: '2px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', flex: 1 }}>
                      <strong>I am a Politically Exposed Person (PEP)</strong> or a family member or close associate of a PEP
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                        This includes current and former PEPs (within the last 12 months)
                      </span>
                    </span>
                  </label>
                </div>

                {/* PEP Details (Conditional) */}
                {formData.pepStatus && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#fff3cd', 
                    borderRadius: '5px',
                    border: '2px solid #ffc107'
                  }}>
                    <p style={{ margin: '0 0 15px 0', fontSize: '13px', fontWeight: 'bold', color: '#856404' }}>
                      ⚠️ Please provide additional information about your PEP status:
                    </p>
                    
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                      PEP Details *
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>
                        Describe your position, relationship to PEP, or close associate details
                      </span>
                    </label>
                    <textarea 
                      name="pepDetails" 
                      value={formData.pepDetails} 
                      onChange={handleChange}
                      placeholder="e.g., 'I am the spouse of [Name], who serves as [Position] in [Country]' or 'I held the position of [Position] from [Year] to [Year]'"
                      rows="4"
                      style={{
                        width: '100%', 
                        padding: '10px',
                        border: errors.pepDetails ? '2px solid #f44336' : '1px solid #ccc',
                        borderRadius: '5px', 
                        fontSize: '13px',
                        backgroundColor: 'white',
                        resize: 'vertical'
                      }}
                    />
                    {errors.pepDetails && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.pepDetails}</p>}

                    <div style={{ 
                      marginTop: '15px', 
                      padding: '12px', 
                      backgroundColor: '#fff9e6', 
                      borderRadius: '5px',
                      fontSize: '12px',
                      color: '#856404'
                    }}>
                      <strong>📌 Note:</strong> Enhanced Due Diligence (EDD) will be required for PEP relationships. 
                      Additional documentation and MLRO approval may be necessary.
                    </div>
                  </div>
                )}

                {/* Non-PEP Declaration */}
                {!formData.pepStatus && (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '5px',
                    fontSize: '13px',
                    color: '#2e7d32'
                  }}>
                    ✓ You have declared that you are <strong>NOT</strong> a Politically Exposed Person, nor a family member or close associate of a PEP.
                  </div>
                )}
              </div>

              {/* ============================================ */}
              {/* SECTION B: SANCTIONS SCREENING INFO */}
              {/* ============================================ */}
              <div style={{ 
                marginBottom: '30px', 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  🛡️ Sanctions & Watchlist Screening
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                    (FATF Rec.12, CBUAE Art.9)
                  </span>
                </h4>

                <div style={{
                  backgroundColor: 'white',
                  padding: '15px',
                  borderRadius: '5px',
                  fontSize: '13px',
                  color: '#666',
                  lineHeight: '1.7'
                }}>
                  <p style={{ margin: '0 0 10px 0' }}>
                    <strong>Automated Screening:</strong> Your information will be automatically screened against:
                  </p>
                  <ul style={{ margin: '0 0 15px 0', paddingLeft: '20px' }}>
                    <li>UN Security Council Sanctions Lists</li>
                    <li>UAE Local Terrorist Lists</li>
                    <li>OFAC (US Treasury) Sanctions Lists</li>
                    <li>EU Sanctions Lists</li>
                    <li>UK HM Treasury Sanctions</li>
                    <li>Interpol Wanted Lists</li>
                    <li>Global PEP Databases</li>
                  </ul>
                  <p style={{ margin: '0', fontSize: '12px', color: '#999' }}>
                    This screening is performed in real-time and is a mandatory compliance requirement. 
                    Any matches will be reviewed by our compliance team.
                  </p>
                </div>
              </div>

              {/* ============================================ */}
              {/* SECTION C: DATA PRIVACY CONSENT */}
              {/* ============================================ */}
              <div style={{ 
                marginBottom: '25px', 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  🔒 Data Privacy & Consent
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '5px' }}>
                    (UAE Data Protection Law Art.40, GDPR Compliant)
                  </span>
                </h4>

                <div style={{
                  backgroundColor: 'white',
                  padding: '15px',
                  borderRadius: '5px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: '#666',
                  lineHeight: '1.7'
                }}>
                  <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#0d47a1' }}>
                    By submitting this form, you consent to the following:
                  </p>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px' }}>
                      1. Data Collection & Processing
                    </p>
                    <p style={{ margin: '0 0 15px 0', fontSize: '12px', paddingLeft: '15px' }}>
                      We will collect, process, and store your personal data including identification documents, 
                      financial information, and transaction details for the purposes of KYC/AML compliance, 
                      customer onboarding, and ongoing relationship management.
                    </p>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px' }}>
                      2. Sanctions & PEP Screening
                    </p>
                    <p style={{ margin: '0 0 15px 0', fontSize: '12px', paddingLeft: '15px' }}>
                      Your data will be screened against international sanctions lists, PEP databases, and 
                      watchlists. Screening may be performed by third-party service providers.
                    </p>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px' }}>
                      3. Data Sharing & Disclosure
                    </p>
                    <p style={{ margin: '0 0 15px 0', fontSize: '12px', paddingLeft: '15px' }}>
                      We may share your information with regulatory authorities, law enforcement agencies, 
                      and service providers as required by UAE AML/CFT laws and regulations.
                    </p>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px' }}>
                      4. Data Retention
                    </p>
                    <p style={{ margin: '0 0 15px 0', fontSize: '12px', paddingLeft: '15px' }}>
                      Your data will be retained for a minimum of 5 years after the relationship ends, 
                      or as required by applicable laws and regulations.
                    </p>
                  </div>

                  <div style={{ marginBottom: '0' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px' }}>
                      5. Your Rights
                    </p>
                    <p style={{ margin: '0', fontSize: '12px', paddingLeft: '15px' }}>
                      You have the right to access, correct, or request deletion of your personal data, 
                      subject to legal and regulatory obligations. Contact our Data Protection Officer for 
                      any privacy-related queries.
                    </p>
                  </div>
                </div>

                {/* Consent Checkbox */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'start', 
                    padding: '15px',
                    backgroundColor: 'white', 
                    borderRadius: '5px', 
                    cursor: 'pointer',
                    border: errors.consentAccepted ? '2px solid #f44336' : (formData.consentAccepted ? '2px solid #4caf50' : '2px solid #1976d2')
                  }}>
                    <input 
                      type="checkbox" 
                      name="consentAccepted" 
                      checked={formData.consentAccepted} 
                      onChange={handleChange}
                      style={{ width: '20px', height: '20px', marginRight: '12px', marginTop: '2px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', flex: 1 }}>
                      <strong>I have read and accept the above terms *</strong>
                      <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '8px' }}>
                        I consent to the processing of my personal data in accordance with the{' '}
                        <a href="#" style={{ color: '#1976d2', textDecoration: 'underline' }} onClick={(e) => e.preventDefault()}>
                          Data Privacy Policy
                        </a>
                        {' '}and{' '}
                        <a href="#" style={{ color: '#1976d2', textDecoration: 'underline' }} onClick={(e) => e.preventDefault()}>
                          AML/CFT Policy
                        </a>.
                        I understand that this consent is mandatory for proceeding with the KYC process.
                      </span>
                    </span>
                  </label>
                  {errors.consentAccepted && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '10px', marginLeft: '15px' }}>{errors.consentAccepted}</p>}
                </div>

                {formData.consentAccepted && (
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '5px',
                    fontSize: '12px',
                    color: '#2e7d32'
                  }}>
                    ✓ Consent accepted on {new Date().toLocaleString('en-GB', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })} • Consent Version: 1.0
                  </div>
                )}
              </div>

              {/* Final Submission Summary */}
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #757575'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#424242', fontSize: '15px' }}>
                  📋 Submission Checklist
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: formData.fullName ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontSize: '14px'
                    }}>
                      {formData.fullName ? '✓' : '1'}
                    </span>
                    <span style={{ color: formData.fullName ? '#2e7d32' : '#666' }}>
                      Personal Information Completed
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: formData.residentialAddress ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontSize: '14px'
                    }}>
                      {formData.residentialAddress ? '✓' : '2'}
                    </span>
                    <span style={{ color: formData.residentialAddress ? '#2e7d32' : '#666' }}>
                      Address & Contact Completed
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: (formData.documents.emiratesIdFront && formData.documents.emiratesIdBack) ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontSize: '14px'
                    }}>
                      {(formData.documents.emiratesIdFront && formData.documents.emiratesIdBack) ? '✓' : '3'}
                    </span>
                    <span style={{ color: (formData.documents.emiratesIdFront && formData.documents.emiratesIdBack) ? '#2e7d32' : '#666' }}>
                      ID Documents Uploaded
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: formData.sourceOfFunds ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontSize: '14px'
                    }}>
                      {formData.sourceOfFunds ? '✓' : '4'}
                    </span>
                    <span style={{ color: formData.sourceOfFunds ? '#2e7d32' : '#666' }}>
                      Source of Funds Declared
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: formData.purposeOfRelationship ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontSize: '14px'
                    }}>
                      {formData.purposeOfRelationship ? '✓' : '5'}
                    </span>
                    <span style={{ color: formData.purposeOfRelationship ? '#2e7d32' : '#666' }}>
                      Expected Activity Defined
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: formData.consentAccepted ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontSize: '14px'
                    }}>
                      {formData.consentAccepted ? '✓' : '6'}
                    </span>
                    <span style={{ color: formData.consentAccepted ? '#2e7d32' : '#666' }}>
                      Consent Accepted
                    </span>
                  </div>
                </div>

                {formData.consentAccepted && formData.fullName && formData.sourceOfFunds && (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '5px',
                    border: '2px solid #4caf50'
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#2e7d32', fontWeight: 'bold' }}>
                      ✓ Ready for Submission!
                    </p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                      Click "Submit for Review" below to complete your KYC application. Our compliance team will review your submission within 2-3 business days.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NAVIGATION BUTTONS */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
            {currentStep > 1 && (
              <button 
                type="button" 
                onClick={goToPreviousStep}
                disabled={saving || loading}
                style={{
                  flex: 1, 
                  backgroundColor: '#757575', 
                  color: 'white',
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '5px',
                  cursor: (saving || loading) ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold', 
                  fontSize: '16px'
                }}
              >
                ← Previous
              </button>
            )}
            
            {/* 🆕 SAVE DRAFT BUTTON */}
            <button 
              type="button" 
              onClick={handleSaveDraft}
              disabled={saving || loading}
              style={{
                flex: 1, 
                backgroundColor: saving ? '#ccc' : '#ff9800', 
                color: 'white',
                border: 'none', 
                padding: '14px', 
                borderRadius: '5px',
                cursor: (saving || loading) ? 'not-allowed' : 'pointer', 
                fontWeight: 'bold', 
                fontSize: '16px'
              }}
            >
              {saving ? '💾 Saving...' : '💾 Save Draft'}
            </button>
            
            {currentStep < 6 ? (
              <button 
                type="button" 
                onClick={goToNextStep}
                disabled={saving || loading}
                style={{
                  flex: 1, 
                  backgroundColor: '#1976d2', 
                  color: 'white',
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '5px',
                  cursor: (saving || loading) ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold', 
                  fontSize: '16px'
                }}
              >
                Next →
              </button>
            ) : (
              <button 
                type="submit" 
                disabled={loading || saving}
                style={{
                  flex: 1, 
                  backgroundColor: (loading || saving) ? '#ccc' : '#4caf50', 
                  color: 'white',
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '5px',
                  cursor: (loading || saving) ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold', 
                  fontSize: '16px'
                }}
              >
                {loading ? 'Submitting...' : '✓ Submit for Review'}
              </button>
            )}
            
            <button 
              type="button" 
              onClick={onClose} 
              disabled={loading || saving}
              style={{
                flex: 1, 
                backgroundColor: '#ccc', 
                color: '#333', 
                border: 'none',
                padding: '14px', 
                borderRadius: '5px',
                cursor: (loading || saving) ? 'not-allowed' : 'pointer', 
                fontWeight: 'bold', 
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default IndividualKYCForm